import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { PDFReportSettingsInfo } from "helpers/types";

const DEFAULT_SETTINGS: PDFReportSettingsInfo = {
  client_name: "",
  account: "",
  period: ["", ""],
  symbol: "",
};

type ReportSettingsContextValue = {
  settingsInfo: PDFReportSettingsInfo;
  updateSettings: (partial: Partial<PDFReportSettingsInfo>) => void;
};

const ReportSettingsContext = createContext<ReportSettingsContextValue | null>(
  null,
);

export function ReportSettingsProvider({ children }: { children: ReactNode }) {
  const [settingsInfo, setSettingsInfo] =
    useState<PDFReportSettingsInfo>(DEFAULT_SETTINGS);

  const updateSettings = useCallback(
    (partial: Partial<PDFReportSettingsInfo>) => {
      setSettingsInfo((prev) => ({ ...prev, ...partial }));
    },
    [],
  );

  const value = useMemo(
    () => ({ settingsInfo, updateSettings }),
    [settingsInfo, updateSettings],
  );

  return (
    <ReportSettingsContext.Provider value={value}>
      {children}
    </ReportSettingsContext.Provider>
  );
}

export function useReportSettings(): ReportSettingsContextValue {
  const ctx = useContext(ReportSettingsContext);
  if (!ctx) {
    return {
      settingsInfo: DEFAULT_SETTINGS,
      updateSettings: () => {},
    };
  }
  return ctx;
}
